import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, LogOut, Mail, Search, Loader2 } from "lucide-react";
import { Sigil } from "@/components/temple/Sigil";
import SessionsAdmin from "@/components/temple/SessionsAdmin";
import InitiatesAdmin from "@/components/temple/InitiatesAdmin";
import QuantumSegmentsAdmin from "@/components/temple/QuantumSegmentsAdmin";
import ReflectionsAdmin from "@/components/temple/ReflectionsAdmin";
import MasteryAdmin from "@/components/temple/MasteryAdmin";
import LettersAdmin from "@/components/temple/LettersAdmin";
import AnalyticsAdmin from "@/components/temple/AnalyticsAdmin";
import BodyMapAdmin from "@/components/temple/BodyMapAdmin";
import AdminTools from "@/components/temple/AdminTools";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;
const http = axios.create({ baseURL: API, withCredentials: true });
const LABELS = { "starter-guide": "Starter Guide", workshop: "Workshop", books: "Books", all: "All of it" };

const errText = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg ?? "").join(" ");
  return d?.msg ?? String(d);
};

const withRefresh = async (fn) => {
  try {
    return await fn();
  } catch (e) {
    if (e.response?.status !== 401) throw e;
    await http.post("/auth/refresh");
    return fn();
  }
};

const Login = ({ onAuthed }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      if (data?.role !== "admin") {
        await http.post("/auth/logout");
        setError("This inbox is for the temple's founder only.");
        return;
      }
      onAuthed(data);
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const field = "w-full bg-transparent border-b border-[#D9B47A]/30 py-4 text-lg placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors";
  return (
    <main className="min-h-screen flex items-center justify-center px-5">
      <form onSubmit={submit} data-testid="inbox-login-form" className="w-full max-w-md">
        <Sigil animate className="w-16 h-16" width={2.5} />
        <h1 className="font-display text-5xl mt-8">The <em className="text-[#D9B47A]">Inner</em> Sanctum</h1>
        <p className="mt-3 text-[#A8998C]">Private enquiry inbox. Priestess access only.</p>
        <input data-testid="inbox-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={`${field} mt-10`} />
        <input data-testid="inbox-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={`${field} mt-4`} />
        {error && <p data-testid="inbox-login-error" className="mt-4 text-sm text-[#E8838E]">{error}</p>}
        <button data-testid="inbox-login-submit" disabled={busy} className="mt-10 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#D9B47A] text-[#0A0706] py-4 font-tt-mono text-[12px] uppercase tracking-[0.25em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Enter
        </button>
        <Link to="/" className="block mt-6 text-center text-sm text-[#A8998C] hover:text-[#D9B47A]">Return to the temple</Link>
      </form>
    </main>
  );
};

const Row = ({ q, onToggle }) => {
  const [open, setOpen] = useState(false);
  const date = q?.created_at ? new Date(q.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
  return (
    <li data-testid={`inbox-row-${q.id}`} className={`border-b border-[#D9B47A]/15 transition-colors ${q.handled ? "opacity-60" : ""}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`inbox-row-toggle-${q.id}`} className="w-full text-left grid grid-cols-12 gap-4 items-center py-5 hover:bg-[#120C0A] px-2">
        <span className="col-span-12 sm:col-span-4 font-display text-xl flex items-center gap-3">
          {!q.handled && <span className="w-2 h-2 rounded-full bg-[#C2414F] shrink-0" />}
          {q.name}
        </span>
        <span className="col-span-7 sm:col-span-4 text-sm text-[#CFC2B6] truncate">{q.email}</span>
        <span className="col-span-5 sm:col-span-2">
          <span className="rounded-full border border-[#D9B47A]/40 px-3 py-1 text-[12px] text-[#D9B47A]">{LABELS[q.interest] ?? q.interest}</span>
        </span>
        <span className="hidden sm:block sm:col-span-2 text-right font-tt-mono text-[12px] text-[#A8998C]">{date}</span>
      </button>
      {open && (
        <div className="px-2 pb-6 grid sm:grid-cols-12 gap-4">
          <p className="sm:col-span-8 text-[15px] text-[#DCCFC3] leading-relaxed whitespace-pre-wrap">
            {q.session_label && <span data-testid={`inbox-session-${q.id}`} className="block mb-2 font-tt-mono text-[12px] uppercase tracking-[0.15em] text-[#C2414F]">Seat requested · {q.session_label}</span>}
            {q.message || <em className="text-[#8F8176]">No message.</em>}
          </p>
          <div className="sm:col-span-4 flex sm:justify-end gap-2 items-start flex-wrap">
            <span className="sm:hidden font-tt-mono text-[12px] text-[#A8998C] w-full">{date}</span>
            {q.alert && (
              <span data-testid={`inbox-alert-${q.id}`} className="w-full sm:text-right font-tt-mono text-[11px] uppercase tracking-[0.15em] text-[#A8998C]">
                {q.alert === "sent" ? "Alert emailed to you" : q.alert === "failed" ? "Alert email failed" : "Alert sending…"}
              </span>
            )}
            {q.guide && (
              <span data-testid={`inbox-guide-${q.id}`} className="w-full sm:text-right font-tt-mono text-[11px] uppercase tracking-[0.15em] text-[#D9B47A]">
                {{ sent: "Starter Guide delivered", failed: "Starter Guide failed to send", "already-sent": "Starter Guide already sent today" }[q.guide] ?? ""}
              </span>
            )}
            <a data-testid={`inbox-reply-${q.id}`} href={`mailto:${q.email}?subject=${encodeURIComponent("The Tantric Temple — " + (LABELS[q.interest] ?? ""))}`} className="inline-flex items-center gap-2 rounded-full border border-[#D9B47A]/50 px-4 py-2 text-sm hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">
              <Mail className="w-4 h-4" /> Reply
            </a>
            <button data-testid={`inbox-handled-${q.id}`} onClick={() => onToggle(q)} className="inline-flex items-center gap-2 rounded-full border border-[#C2414F]/60 px-4 py-2 text-sm hover:bg-[#C2414F] transition-colors">
              <Check className="w-4 h-4" /> {q.handled ? "Mark as new" : "Mark handled"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("everything");
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") ?? "enquiries");
  useEffect(() => {
    document.querySelector(`[data-testid="inbox-tab-${tab}"]`)?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [tab]);

  const load = useCallback(async () => {
    try {
      const { data } = await withRefresh(() => http.get("/admin/inquiries"));
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) onLogout();
      else toast.error("Could not load enquiries.");
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (q) => {
    try {
      await withRefresh(() => http.patch(`/admin/inquiries/${q.id}`, { handled: !q.handled }));
      setItems((list) => list.map((x) => (x.id === q.id ? { ...x, handled: !q.handled } : x)));
    } catch {
      toast.error("Could not update this enquiry.");
    }
  };

  const shown = useMemo(() => {
    const list = items ?? [];
    const s = query.trim().toLowerCase();
    return list.filter((q) => {
      if (filter === "new" && q.handled) return false;
      if (!["everything", "new"].includes(filter) && q.interest !== filter) return false;
      return !s || `${q.name} ${q.email} ${q.message}`.toLowerCase().includes(s);
    });
  }, [items, filter, query]);

  const list = items ?? [];
  const stats = [
    ["Total", list.length],
    ["New", list.filter((q) => !q.handled).length],
    ["Starter Guide", list.filter((q) => q.interest === "starter-guide").length],
    ["Workshop", list.filter((q) => q.interest === "workshop" || q.interest === "all").length],
  ];

  return (
    <main className="min-h-screen max-w-[1200px] mx-auto px-5 sm:px-10 py-10">
      <header className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Sigil className="w-8 h-8" width={3.5} />
          <span className="font-display text-lg">The Tantric <em className="text-[#D9B47A]">Temple</em></span>
        </Link>
        <button data-testid="inbox-logout" onClick={onLogout} className="inline-flex items-center gap-2 text-sm text-[#CFC2B6] hover:text-[#D9B47A]">
          <LogOut className="w-4 h-4" /> Leave
        </button>
      </header>
      <h1 className="font-display text-4xl sm:text-6xl mt-16">Enquiry <em className="text-[#D9B47A]">Inbox</em></h1>
      <p className="mt-3 text-[#A8998C]">Welcome back, {user?.name || "Priestess"}.</p>
      <div className="mt-10 flex gap-4 sm:gap-6 overflow-x-auto border-b border-[#D9B47A]/15">
        {[["enquiries", "Enquiries"], ["analytics", "Analytics"], ["initiates", "Initiates"], ["gates13", "13 Gates app"], ["quantum", "Quantum app"], ["bodymap", "Body map"], ["tools", "Tools"], ["reflections", "Reflections"], ["mastery", "Honour roll"], ["letters", "Letters"], ["sessions", "Workshop dates"]].map(([k, l]) => (
          <button key={k} data-testid={`inbox-tab-${k}`} onClick={() => setTab(k)} className={`pb-3 -mb-px border-b-2 whitespace-nowrap font-tt-mono text-[11px] sm:text-[12px] uppercase tracking-[0.12em] sm:tracking-[0.25em] transition-colors ${tab === k ? "border-[#D9B47A] text-[#F2E8DF]" : "border-transparent text-[#A8998C] hover:text-[#CFC2B6]"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "analytics" ? <AnalyticsAdmin http={http} withRefresh={withRefresh} /> : tab === "tools" ? <AdminTools http={http} withRefresh={withRefresh} /> : tab === "bodymap" ? <BodyMapAdmin http={http} withRefresh={withRefresh} /> : tab === "quantum" ? <QuantumSegmentsAdmin key="quantum" http={http} withRefresh={withRefresh} /> : tab === "gates13" ? <QuantumSegmentsAdmin key="gates13" kind="gates13" http={http} withRefresh={withRefresh} /> : tab === "sessions" ? <SessionsAdmin http={http} withRefresh={withRefresh} /> : tab === "initiates" ? <InitiatesAdmin http={http} withRefresh={withRefresh} /> : tab === "reflections" ? <ReflectionsAdmin http={http} withRefresh={withRefresh} /> : tab === "mastery" ? <MasteryAdmin http={http} withRefresh={withRefresh} /> : tab === "letters" ? <LettersAdmin http={http} withRefresh={withRefresh} /> : (<>

      <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#D9B47A]/20 border border-[#D9B47A]/20">
        {stats.map(([k, v]) => (
          <div key={k} className="bg-[#0D0908] p-6">
            <p data-testid={`inbox-stat-${k.toLowerCase().replace(/\s/g, "-")}`} className="font-display text-4xl text-[#D9B47A]">{v}</p>
            <p className="font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#A8998C] mt-2">{k}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {[["everything", "Everything"], ["new", "New"], ...Object.entries(LABELS)].map(([k, l]) => (
            <button key={k} data-testid={`inbox-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full border px-4 py-2 text-sm transition-colors ${filter === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/30 text-[#CFC2B6] hover:border-[#D9B47A]"}`}>
              {l}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 border-b border-[#D9B47A]/30 py-2 lg:w-72">
          <Search className="w-4 h-4 text-[#A8998C]" />
          <input data-testid="inbox-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, message" className="bg-transparent flex-1 text-sm placeholder:text-[#8F8176] focus:outline-none" />
        </label>
      </div>

      {items === null ? (
        <p className="mt-16 text-[#A8998C] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Opening the sanctum…</p>
      ) : shown.length === 0 ? (
        <p data-testid="inbox-empty" className="mt-16 font-display italic text-2xl text-[#A8998C]">No enquiries here yet.</p>
      ) : (
        <ul data-testid="inbox-list" className="mt-8 border-t border-[#D9B47A]/15">
          {shown.map((q) => <Row key={q.id} q={q} onToggle={toggle} />)}
        </ul>
      )}
      </>)}
    </main>
  );
};

export default function Inbox() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    let alive = true;
    withRefresh(() => http.get("/auth/me"))
      .then(({ data }) => alive && setUser(data?.role === "admin" ? data : false))
      .catch(() => alive && setUser(false));
    return () => {
      alive = false;
    };
  }, []);
  const logout = useCallback(async () => {
    try {
      await http.post("/auth/logout");
    } finally {
      setUser(false);
    }
  }, []);

  if (user === null) return <main className="min-h-screen flex items-center justify-center"><Sigil animate className="w-16 h-16" width={2.5} /></main>;
  if (!user) return <Login onAuthed={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
